import React, { useState, useEffect, useRef } from "react";
import {
    Box, Typography, TextField, Button, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, Grid, Autocomplete, TablePagination
} from "@mui/material";
import { Delete, Edit, Add, Search, DeleteOutline, Close } from "@mui/icons-material";
import {
    collection, addDoc, updateDoc, deleteDoc, doc,
    query, orderBy, onSnapshot,
    getDoc
} from "firebase/firestore";
import { db } from "../../firebase";
import { Print } from "@mui/icons-material";
import { useReactToPrint } from "react-to-print";
import PrintQuot from "./PrintQuot";

function Tripquotation() {
    const [customers, setCustomers] = useState([]);
    const [products, setProducts] = useState([]);
    const [invoices, setInvoices] = useState([]);

    const [open, setOpen] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [search, setSearch] = useState("");

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [printOpen, setPrintOpen] = useState(false);
    const [printData, setPrintData] = useState(null);
    const [printCustomer, setPrintCustomer] = useState(null);
    const initialForm = {
        customerId: "",
        customerName: "",
        invoiceDate: new Date().toISOString().split("T")[0],
        invoiceNo: "",
        items: [{ productName: "", hsnCode: "", qty: 1, rate: 0, total: 0 }],
        paidAmount: 0,
    };

    const [formData, setFormData] = useState(initialForm);

    const componentRef = useRef();

    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
        documentTitle: `Invoice_${printData?.invoiceNo}`,
        removeAfterPrint: true,
    });

    useEffect(() => {
        if (printOpen && componentRef.current) {
            setTimeout(() => {
                handlePrint();
            }, 500); // small delay to ensure render
        }
    }, [printOpen]);

    // FETCH DATA
    useEffect(() => {
        const unsubCust = onSnapshot(
            query(collection(db, "customers"), orderBy("customerName")),
            (snap) => {
                setCustomers(
                    snap.docs.map(d => ({
                        id: d.id,
                        name: d.data().customerName,
                        mobile: d.data().mobile || "",
                        email: d.data().email || "",
                        address: d.data().address || ""
                    }))
                );
            }
        );

        const unsubProd = onSnapshot(collection(db, "trip_products"), (snap) => {
            setProducts(snap.docs.map(d => ({
                name: d.data().productName || "",
                rate: d.data().rate || 0,
                hsnCode: d.data().hsnCode || ""
            })));
        });

        const unsubInv = onSnapshot(
            query(collection(db, "tripQuots"), orderBy("invoiceDate", "desc")),
            (snap) => setInvoices(snap.docs.map(d => ({ id: d.id, ...d.data() })))
        );

        return () => { unsubCust(); unsubProd(); unsubInv(); };
    }, []);

    // AUTO INVOICE GENERATION
    const generateInvoiceNo = () => {
        if (invoices.length === 0) return "INV-0001";
        // Sort by number to find the highest
        const lastNo = invoices
            .map(inv => parseInt(inv.invoiceNo?.replace("INV-", "") || 0))
            .sort((a, b) => b - a)[0];
        return `INV-${String(lastNo + 1).padStart(4, "0")}`;
    };

    // CALCULATIONS
    const calculateTotals = (items, paid) => {
        const subTotal = items.reduce((sum, i) => sum + (Number(i.total) || 0), 0);
        const cgst = subTotal * 0.025;
        const sgst = subTotal * 0.025;
        const grandTotal = subTotal + cgst + sgst;
        const remaining = grandTotal - (Number(paid) || 0);
        return {
            subTotal: subTotal || 0,
            cgst: cgst || 0,
            sgst: sgst || 0,
            grandTotal: grandTotal || 0,
            remaining: remaining || 0
        };
    };

    const updateItem = (index, field, value) => {
        const updated = [...formData.items];
        updated[index][field] = value;

        if (field === "productName") {
            const prod = products.find(p => p.name === value);
            if (prod) {
                updated[index].rate = prod.rate || 0;
                updated[index].hsnCode = prod.hsnCode || "";
            }
        }

        updated[index].total = (Number(updated[index].qty) || 0) * (Number(updated[index].rate) || 0);
        setFormData({ ...formData, items: updated });
    };

    const removeItem = (index) => {
        const updated = formData.items.filter((_, i) => i !== index);
        setFormData({ ...formData, items: updated });
    };

    // SAVE WITH SANITIZATION
    const handleSave = async () => {
        const calc = calculateTotals(formData.items, formData.paidAmount);

        // Create payload and remove any 'undefined' values
        const payload = JSON.parse(JSON.stringify({
            ...formData,
            ...calc,
            customerName: formData.customerName || "Walk-in Customer",
            customerId: formData.customerId,

            invoiceNo: formData.invoiceNo || generateInvoiceNo()
        }));

        try {
            if (selectedId) {
                await updateDoc(doc(db, "tripQuots", selectedId), payload);
            } else {
                await addDoc(collection(db, "tripQuots"), payload);
            }
            setOpen(false);
            setFormData(initialForm);
            setSelectedId(null);
        } catch (error) {
            console.error("Error saving document: ", error);
            alert("Error saving: " + error.message);
        }
    };

    const filtered = invoices.filter(inv =>
        inv.customerName?.toLowerCase().includes(search.toLowerCase()) ||
        inv.invoiceNo?.toLowerCase().includes(search.toLowerCase())
    );

    const totals = calculateTotals(formData.items, formData.paidAmount);

    const handleWindowPrint = () => {
        const content = document.getElementById("print-area");

        if (!content) {
            alert("Print content not found");
            return;
        }

        const printWindow = window.open("", "", "width=900,height=650");

        printWindow.document.write(`
        <html>
        <head>
            <title>Quotation ${printData?.invoiceNo}</title>

            <style>
                body {
                    margin: 0;
                    padding: 15px;
                    font-family: Arial, sans-serif;
                }

                .invoice-container {
                    width: 800px;
                    margin: auto;
                }

                table {
                    width: 100%;
                    border-collapse: collapse;
                }

                th, td {
                    border: 1px solid black;
                    padding: 5px;
                    font-size: 12px;
                }

                th {
                    background: #f2f2f2;
                }

                @page {
                    size: A4;
                    margin: 10mm;
                }
            </style>
        </head>

        <body>
            <div class="invoice-container">
                ${content.innerHTML}
            </div>
        </body>
        </html>
    `);

        printWindow.document.close();

        setTimeout(() => {
            printWindow.focus();
            printWindow.print();
            printWindow.close();
        }, 500);
    };

    return (
        <Box p={1}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4" fontWeight="bold" color="primary">Trip Quotation</Typography>
                <Box display="flex" gap={2}>
                    <TextField
                        size="small"
                        placeholder="Search..."
                        value={search} sx={{ width: 200 }}
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{ startAdornment: <Search sx={{ mr: 1, color: 'gray' }} /> }}
                    />
                    <Button
                        variant="contained"
                        startIcon={<Add />}
                        onClick={() => {
                            setFormData({ ...initialForm, invoiceNo: generateInvoiceNo() });
                            setOpen(true);
                            setSelectedId(null);
                        }}
                    >
                        New Quotation
                    </Button>
                </Box>
            </Box>

            <TableContainer component={Paper} elevation={3}>
                <Table size="small">
                    <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                        <TableRow>
                            <TableCell><b>Sr. No</b></TableCell>
                            <TableCell><b>Date</b></TableCell>
                            <TableCell><b>Quotation No</b></TableCell>
                            <TableCell><b>Customer</b></TableCell>
                            <TableCell><b>Total (Inc. GST)</b></TableCell>
                            <TableCell><b>Balance</b></TableCell>
                            <TableCell align="right"><b>Actions</b></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((inv, i) => (
                                <TableRow key={inv.id} hover>
                                    <TableCell>{(page * rowsPerPage) + i + 1}</TableCell>
                                    <TableCell>{inv.invoiceDate}</TableCell>
                                    <TableCell>{inv.invoiceNo}</TableCell>
                                    <TableCell>{inv.customerName}</TableCell>
                                    <TableCell>₹{Number(inv.grandTotal || 0).toFixed(2)}</TableCell>
                                    <TableCell sx={{ color: inv.remaining > 0 ? "error.main" : "success.main", fontWeight: 'bold' }}>
                                        ₹{Number(inv.remaining || 0).toFixed(2)}
                                    </TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" onClick={() => { setFormData(inv); setSelectedId(inv.id); setOpen(true); }}>
                                            <Edit fontSize="small" color="primary" />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => { if (window.confirm("Delete?")) deleteDoc(doc(db, "tripQuots", inv.id)) }}>
                                            <Delete fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            onClick={async () => {
                                                setPrintData(inv);

                                                // 🔥 Fetch full customer details
                                                if (inv.customerId) {
                                                    const snap = await getDoc(doc(db, "customers", inv.customerId));
                                                    if (snap.exists()) {
                                                        setPrintCustomer(snap.data());
                                                    }
                                                }

                                                setPrintOpen(true);
                                            }}

                                        >
                                            <Print />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filtered.length}
                    page={page}
                    rowsPerPage={rowsPerPage}
                    onPageChange={(e, p) => setPage(p)}
                    onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                />
            </TableContainer>

            <Dialog open={open} fullWidth maxWidth="lg">
                <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', mb: 2 }}>
                    {selectedId ? "Edit" : "New"} Quotation: {formData.invoiceNo}
                </DialogTitle>
                <DialogContent>
                    <Grid className="row" spacing={3} mt={1}>
                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <Autocomplete
                                options={customers}
                                getOptionLabel={(opt) => opt.name || ""}
                                value={customers.find(c => c.id === formData.customerId) || null}
                                onChange={(e, val) => {
                                    setFormData({
                                        ...formData,
                                        customerId: val?.id || "",
                                        customerName: val?.name || ""
                                    });
                                }}
                                renderInput={(params) =>
                                    <TextField {...params} label="Customer" size="small" />
                                }
                            />
                        </Grid>
                        <Grid className="col-sm-6 mb-3" item xs={12} md={6}>
                            <TextField
                                type="date" label="Date" fullWidth size="small"
                                value={formData.invoiceDate}
                                onChange={(e) => setFormData({ ...formData, invoiceDate: e.target.value })}
                                InputLabelProps={{ shrink: true }}
                            />
                        </Grid>

                        {/* LINE ITEMS WITH GST DISPLAY */}
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>Product Details</Typography>
                            {formData.items.map((item, i) => (
                                <Box key={i} display="flex" gap={1} mb={1} alignItems="center" sx={{ borderBottom: '1px solid #eee', pb: 1 }}>
                                    <Typography variant="body2" sx={{ minWidth: 25 }}>{i + 1}.</Typography>
                                    <Autocomplete
                                        sx={{ flex: 3 }}
                                        options={products.map(p => p.name)}
                                        value={item.productName || null}
                                        onChange={(e, v) => updateItem(i, "productName", v)}
                                        renderInput={(params) => <TextField {...params} label="Product" size="small" />}
                                    />
                                    <TextField label="HSN" size="small" sx={{ flex: 1 }} value={item.hsnCode} onChange={(e) => updateItem(i, "hsnCode", e.target.value)} />
                                    <TextField label="Qty" type="number" size="small" sx={{ flex: 1 }} value={item.qty} onChange={(e) => updateItem(i, "qty", e.target.value)} />
                                    <TextField label="Rate" type="number" size="small" sx={{ flex: 1 }} value={item.rate} onChange={(e) => updateItem(i, "rate", e.target.value)} />

                                    {/* Row Level GST Visuals */}
                                    <TextField label="CGST(2.5%)" size="small" sx={{ flex: 1 }} value={(item.total * 0.025).toFixed(2)} disabled />
                                    <TextField label="SGST(2.5%)" size="small" sx={{ flex: 1 }} value={(item.total * 0.025).toFixed(2)} disabled />

                                    <TextField label="Total" size="small" sx={{ flex: 1 }} value={item.total.toFixed(2)} disabled sx={{ bgcolor: '#f9f9f9' }} />
                                    <IconButton onClick={() => removeItem(i)} disabled={formData.items.length === 1} color="error">
                                        <DeleteOutline fontSize="small" />
                                    </IconButton>
                                </Box>
                            ))}
                            <Button startIcon={<Add />} onClick={() => setFormData({ ...formData, items: [...formData.items, { productName: "", hsnCode: "", qty: 1, rate: 0, total: 0 }] })}>
                                Add Product
                            </Button>
                        </Grid>

                        {/* TOTALS SECTION */}
                        <Grid item xs={12} md={7}></Grid>
                        <Grid item xs={12} md={5}>
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fcfcfc' }}>
                                <Box display="flex" justifyContent="space-between" mb={1}>
                                    <Typography>Taxable Amount:</Typography>
                                    <Typography>₹{totals.subTotal.toFixed(2)}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" mb={1} color="text.secondary">
                                    <Typography>CGST (2.5%):</Typography>
                                    <Typography>₹{totals.cgst.toFixed(2)}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" mb={1} color="text.secondary">
                                    <Typography>SGST (2.5%):</Typography>
                                    <Typography>₹{totals.sgst.toFixed(2)}</Typography>
                                </Box>
                                <Box display="flex" justifyContent="space-between" mb={2} pt={1} sx={{ borderTop: '2px solid #1976d2' }}>
                                    <Typography variant="h6"><b>Grand Total:</b></Typography>
                                    <Typography variant="h6" color="primary"><b>₹{totals.grandTotal.toFixed(2)}</b></Typography>
                                </Box>
                                <TextField
                                    label="Amount Received" type="number" fullWidth size="small"
                                    value={formData.paidAmount}
                                    onChange={(e) => setFormData({ ...formData, paidAmount: e.target.value })}
                                />
                                <Box display="flex" justifyContent="space-between" mt={2}>
                                    <Typography fontWeight="bold">Balance Due:</Typography>
                                    <Typography fontWeight="bold" color={totals.remaining > 0 ? "error.main" : "success.main"}>
                                        ₹{totals.remaining.toFixed(2)}
                                    </Typography>
                                </Box>
                            </Paper>
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions sx={{ p: 3, bgcolor: '#f5f5f5' }}>
                    <Button onClick={() => setOpen(false)} color="inherit">Cancel</Button>
                    <Button variant="contained" onClick={handleSave} size="large">Save Quotation</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={printOpen} fullWidth maxWidth="md">
                <DialogTitle sx={{ bgcolor: '#f5f5f5', display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="h6">Quotation Preview</Typography>
                    <IconButton onClick={() => setPrintOpen(false)} size="small"><Close /></IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 0 }}> {/* No padding here so the invoice fits edge to edge */}
                    <Box id="print-area" ref={componentRef}>
                        <PrintQuot data={printData} customer={printCustomer} />
                    </Box>
                </DialogContent>

                <DialogActions sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                    <Button onClick={() => setPrintOpen(false)} color="inherit">Cancel</Button>
                    <Button
                        variant="contained"
                        color="primary"

                        onClick={handleWindowPrint}
                        startIcon={<Print />}
                        sx={{ fontWeight: 'bold', px: 4 }}
                    >
                        Print Quotation
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default Tripquotation;